import * as moment from 'moment';
import { Utils } from './Utils';

const momentLocal = moment;
const momentUtc = moment.utc;

export type DateType = Date | string | moment.Moment;

export type Time = {
    hours: number;
    minutes: number;
    seconds?: number;
    period?: 'AM' | 'PM';
}

export enum DateFormatType {
    FullLongDateTime,
    FullShortDateTime,
    Time,
    FullTime
}

export enum DiffType {
    Years = <any>'y',
    Months = <any>'M',
    Days = <any>'d',
    Hours = <any>'h',
    Minutes = <any>'m',
    Seconds = <any>'s',
    Milliseconds = <any>'ms',
}

//const defaultDateFormat24 = 'DD.MM.Y HH:mm:ss';
const Formats = {
    Time: {
        Format24: "HH:mm",
        Format24Seconds: "HH:mm:ss",
        FormatMeridian: "h:mm tt",
        FormatMeridianSeconds: "h:mm:ss tt"
    },

    Date: {
        Format: 'DD.MM.Y'
    },

    DateTime: {
        Format24Seconds: 'DD.MM.Y HH:mm:ss',
        Format24: 'DD.MM.Y HH:mm'
    }
};


export class DateUtils {
    public static Formats = Formats;

    public static formatTime(time: Time): string {
        if (Utils.isNullOrUndefined(time)) {
            throw new Error('Parameter "time" is required!');
        }

        var result = `${time.hours}:${time.minutes}`;
        if (!Utils.isNullOrUndefined(time.seconds)) {
            result += `:${time.seconds}`;
        }

        if (!Utils.isNullOrUndefined(time.period)) {
            result += ` ${time.period}`;
        }

        return result;
    }

    public static parseTime(time: string): Time {
        var result: Time = null;

        function parseNumberAndPeriod(value: string): { value: number, period: string } {
            var resValue: number = null;
            var resPeriod: string = null;

            var parsed = Utils.tryParseNumber(value);
            if (parsed.isValid) {
                resValue = parsed.value;
            } else {
                var vals = value.split(' ');
                if (vals !== null && vals.length === 2) {
                    parsed = Utils.tryParseNumber(vals[0]);
                    if (parsed.isValid) {
                        resValue = parsed.value;

                        if (vals[1] === 'AM' || vals[1] === 'PM') {
                            resPeriod = vals[1];
                        }
                    }
                }
            }

            return {
                value: resValue,
                period: resPeriod
            };
        }

        if (!Utils.isNullOrUndefined(time)) {
            var values = time.split(':');
            if (values !== null && values.length > 1) {
                var parsedHour = Utils.tryParseNumber(values[0]);
                var parsedMinute = Utils.tryParseNumber(values[1]);

                if (parsedHour.isValid && parsedMinute.isValid) {
                    result = {
                        hours: parsedHour.value,
                        minutes: parsedMinute.value
                    };

                    if (values.length > 2) {
                        /*var parsedSecond = Utils.tryParseNumber(values[2]);
                        if (parsedSecond.isValid) {
                            result.second = parsedSecond.value;
                        }*/

                        var parsedSecond = parseNumberAndPeriod(values[2]);
                        if (parsedSecond.value !== null) {
                            result.seconds = parsedSecond.value;
                        }

                        if (parsedSecond.period !== null) {
                            result.period = <any>parsedSecond.period;
                        }
                    }
                }
            }
        }

        return result;
    }

    public static getUtcMoment(): moment.Moment {
        return DateUtils._getUtcMoment(false) as moment.Moment;
    }

    public static getUtcMomentAsIso(): string {
        return DateUtils._getUtcMoment(true) as string;
    }

    private static _getUtcMoment(asIso: boolean): moment.Moment | string {
        let result = momentUtc();

        asIso = Utils.valueOrDefault(asIso, false);
        if (asIso) {
            result = <any>result.toISOString();
        }

        return result;
    }

    public static parse(value: string, format: string = Formats.DateTime.Format24Seconds): moment.Moment {
        return moment(value, format);
    }

    public static asUtcMoment(value: DateType): moment.Moment {
        if (value instanceof Date) {
            return momentUtc(value);
        } else if (value instanceof moment) {
            var m = value as moment.Moment;
            if (m && !m.creationData().isUTC) {
                m.utc(); // make utc from local
            }
            return m;
        } else if (typeof value === 'string') {
            return momentUtc(value);
        } else {
            throw new Error('convertToMoment, unknown date type: ' + Object.prototype.toString.call(value));
        }
    }

    public static toIso(date: moment.Moment): string {
        return date.toISOString();
    }

    public static getDateFormatString(type: DateFormatType): string {
        switch (type) {
            case DateFormatType.FullLongDateTime:
                return 'LLL';

            case DateFormatType.FullShortDateTime:
                return 'l LTS';

            case DateFormatType.Time:
                return 'LT';

            case DateFormatType.FullTime:
                return 'LTS';

            default:
                throw new Error(`Unknown DateFormatType value "${type}"`);
        }
    }

    public static toLocal(value: DateType): moment.Moment {
        let utcDate = DateUtils.asUtcMoment(value);
        let localDate = utcDate.toDate();
        return momentLocal(localDate);
    }

    public static isEqual(date1?: DateType, date2?: DateType): boolean {
        var date1IsNull = Utils.isNullOrUndefined(date1);
        var date2IsNull = Utils.isNullOrUndefined(date2);

        if (date1IsNull && date2IsNull) {
            return true;
        }

        if (!date1IsNull && !date2IsNull) {
            var m1 = DateUtils.asUtcMoment(date1);
            var m2 = DateUtils.asUtcMoment(date2);

            return m1.isSame(m2);
        }

        return false;
    }

    public static orderByTime(a: moment.Moment, b: moment.Moment, asc: boolean): number {
        let result = 0;
        if (a.isSame(b)) {
            let a_millis = a.milliseconds();
            let b_millis = b.milliseconds();
            if (a_millis < b_millis) {
                result = -1;
            } else if (a_millis > b_millis) {
                result = 1;
            }
        } else {
            result = a.isAfter(b) ? 1 : -1;
        }

        return asc ? result : -1 * result;
    }

    public static isGreater(source: DateType, than: DateType): boolean {
        var a = DateUtils.asUtcMoment(source);
        var b = DateUtils.asUtcMoment(than);
        return DateUtils.orderByTime(a, b, true) === 1;
    }

    public static orderListBy<T>(list: Array<T>, asc: boolean,
        selector: (item: T) => moment.Moment): Array<T> {

        list.sort((itemA: T, itemB: T) => {
            var a = selector(itemA);
            var b = selector(itemB);
            return DateUtils.orderByTime(a, b, asc);
        });

        return list;
    }

    public static getDiff(date1: DateType, date2: DateType, type: DiffType): number {
        let k = type.toString();
        let m1 = DateUtils.asUtcMoment(date1);
        let m2 = DateUtils.asUtcMoment(date2);
        return m1.diff(m2, k);
    }

    public static addDiff(value: DateType, diff: number, type: DiffType): moment.Moment {
        let k = type.toString();
        let m = moment(DateUtils.asUtcMoment(value)); // this will clone moment!
        return m.add(diff, k);
    }

    public static getUnixTimestamp(): number {
        return Number(momentUtc().format('x'));
    }

    public static asUnixTimestamp(value: DateType): number {
        var m = DateUtils.asUtcMoment(value);
        return Number(m.format('x'));
    }

    public static toDefaultWithTime(value?: DateType): string {
        if (Utils.isNullOrUndefined(value)) {
            return "";
        }

        var a = moment(value);
        return a.format(Formats.DateTime.Format24Seconds);
    }

    public static toDefaultWithTimeExt(value?: DateType, useSeconds: boolean = true): string {
        if (Utils.isNullOrUndefined(value)) {
            return "";
        }

        var a = moment(value);
        return a.format(useSeconds ? Formats.DateTime.Format24Seconds : Formats.DateTime.Format24);
    }
}


/** WEBPACK FOOTER **
 ** ./Typescript/Utils/DateUtils.ts
 **/